// /src/utils/identityReset.js
// Signing out, or signing in as a different account, returns the app to a clean start (Roadmap
// #100; docs/briefs/2026-10-16-sign-out-clears-the-group.md Part 2 and addendum 1). App.js owns the
// one reset (resetForIdentityChange) and calls it from its four auth paths: handleLogout, the
// SIGNED_OUT listener, handleLoginSuccess and the SIGNED_IN listener. This file holds the parts
// that can be tested without rendering App.js: which identity changes reset, and the clean value
// of every piece of App.js state that must not outlive the account it was loaded for.

// The last signed-in identity before the tab has learnt it (cold load still resolving). Unknown is
// NOT a guest: it resets, so a tab whose identity was never recorded fails closed (Advisor SA-1).
export const IDENTITY_UNKNOWN = Symbol('identity-unknown');

/**
 * Does this auth event need a clean start?
 *
 * "Identity change" here is deliberately not the definition used by App.js's setup-gate effect
 * (previousUserIdRef: null→value and value→other-value, 16 Sep stale-session brief) nor by demo
 * mode (demoUserIdRef: whoever entered the demo). This one resets on value→null and
 * value→other-value, but NOT on an observed guest signing in: a guest's screen holds only public
 * content, and a guest who signs in on a group page (an invited newcomer, R5 — see App.js
 * handleSetupComplete) must stay on it. Signing out already reset, so the same account signing
 * back in still starts clean (addendum 1, ruling 4).
 *
 * @param {'signed-out'|'signed-in'} kind
 * @param {string|null|symbol} lastId - last signed-in user id; null = an observed guest;
 *   IDENTITY_UNKNOWN = not yet learnt
 * @param {string|null} nextId - the user now signed in (null for 'signed-out')
 * @returns {boolean}
 */
export function identityResetNeeded(kind, lastId, nextId) {
  if (lastId === IDENTITY_UNKNOWN) return true;
  if (kind === 'signed-out') return lastId !== null;
  return lastId !== null && lastId !== nextId;
}

// Views opened by a link, keyed to that link rather than to an account. An account change while
// one is open (an invitation accepted by signing in as the invited address) keeps the view.
export const LINK_VIEWS = new Set(['accept-invitation', 'join-group-link', 'authority-verify', 'suggest']);

// Every App.js useState that must not survive an identity change, with its clean value. The
// content arrays come first: the previous account's posts, chats and events go before anything
// else (addendum 1, ruling 3). currentView is applied by App.js (LINK_VIEWS keep theirs).
export const CLEAN_START = Object.freeze({
  articles: [],
  articlesHasMore: false,
  chats: [],
  events: [],
  eventsHasMore: { future: false, past: false },
  loadingMore: null,
  searchedArticles: [],
  articleSearchTerm: '',
  selectedArticle: null,
  selectedChat: null,
  selectedChatResponses: [],
  selectedEvent: null,
  currentView: 'feed',
  currentContext: 'locality',
  previousContext: null,
  previousView: null,
  previousGroup: null,
  landingFromGate: false,
  currentCommunity: null,
  selectedGroup: null,
  selectedArea: null,
  visitArea: null,
  currentMode: 'news',
  frontPageActive: true,
  mobileScreen: 'news',
  groupsInitialMode: 'joined',
  feedScrollPosition: 0,
  shouldRestoreScroll: false,
  frontPageOpenChatId: null,
  selectedProfileUserId: null,
  articleMode: 'add',
  articleToEdit: null,
  preselectedGroupId: null,
  chatMode: 'add',
  chatToEdit: null,
  preselectedChatGroupId: null,
  eventMode: 'add',
  eventToEdit: null,
  preselectedEventGroupId: null,
  groupMode: 'add',
  groupToEdit: null,
  postCreateGroup: false,
  parentGroupForNew: null,
  groupPrefill: null,
  addEditLandingPageGroup: null,
  postCreateLandingPage: false,
  landingPageGroup: null,
  showSmartLinkPreview: false,
  smartLinkData: null,
  smartLinkTarget: null,
  smartLinkLoading: false,
  autoHomeHighlight: false,
  myLocationsSetHomeSeed: null,
});

// Every other App.js useState, and why it survives. identityReset.test.js fails if App.js gains a
// useState that is in neither list — decide which it is.
export const KEPT = {
  currentUser: 'set by the auth paths themselves',
  isLoggedIn: 'set by the auth paths themselves',
  showLoginModal: 'closed by handleLogout; a sign-in path closes it itself',
  loginModalMode: 'modal preference, not account data',
  pendingNavigation: 'cleared by handleLogout; on sign-in it is the navigation the sign-in was for',
  authRequiredMessage: 'gate copy, reset when the login modal closes',
  showResetPasswordModal: 'the password-reset link opens it during the sign-in it causes',
  appModal: 'a transient notice, not account data',
  loading: 'load indicator',
  userHomeLocationName: 'replaced by the refresh-locations reload the reset triggers',
  userHomeLocationCoords: 'replaced by the refresh-locations reload the reset triggers',
  detectedBoundaryId: "the visitor's IP location, keyed to no account",
  countryInView: 'the country being viewed, keyed to no account',
  pendingHashNavigation: 'a link being opened, keyed to the link',
  inviteToken: 'a link being opened, keyed to the link',
  joinToken: 'a link being opened, keyed to the link',
  emailActionToken: 'a link being opened, keyed to the link',
  authorityVerifyToken: 'a link being opened, keyed to the link',
  isDemoMode: 'owned by leaveDemoMode, which the auth paths already call',
  demoRouteCheck: 'owned by the demo route',
  demoSessionExpired: 'owned by the demo route',
  signInNotice: 'a notice about the sign-in itself',
  setupState: "owned by its own identity effect (App.js previousUserIdRef)",
};

/**
 * Apply CLEAN_START through App.js's setters. Never throws: a missing setter must not stop a
 * sign-out halfway (Advisor ADVISE 3). Completeness is enforced by the tests instead.
 * @param {Object<string, Function>} setters - keyed like CLEAN_START
 * @param {Object} [overrides] - values that replace CLEAN_START's for this call
 * @returns {string[]} the keys that had no setter
 */
export function applyCleanStart(setters, overrides = {}) {
  const missing = [];
  Object.keys(CLEAN_START).forEach((key) => {
    const set = setters[key];
    if (typeof set !== 'function') {
      missing.push(key);
      return;
    }
    const value = key in overrides ? overrides[key] : CLEAN_START[key];
    set(Array.isArray(value) ? [] : value); // a fresh array each time, never one shared instance
  });
  return missing;
}
